import { create } from 'zustand';
import {
  CanonicalDocument,
  canonicalHash,
  emptyDocument,
  esSoloTrazo,
  hashDeFuente,
  IDENTITY_MATRIX,
  layerBBoxMm,
  proporcionDeForma,
  trazadoDeForma,
  type Layer,
  type NombreForma,
  type OpcionesForma,
  type PointMm,
  type Color,
} from '@besbello/design-core';
import {
  validateDesign,
  type Diagnostic,
  type EffectiveZone,
  type TechniqueRuleVersion,
  type ValidationResult,
} from '@besbello/rules-core';
import { alinear, distribuir, type Alineacion, type Caja } from './encaje.js';
import { agrupar, companerasDeGrupo, desagrupar, grupoDe } from './grupos.js';
import { buscarPlantilla, plantillaEnZona } from './plantillas.js';
import { api } from '../api/client.js';
import {
  deshacer as deshacerPaso,
  historialVacio,
  puedeDeshacer,
  puedeRehacer,
  registrar,
  rehacer as rehacerPaso,
  type Historial,
} from './historial.js';

/**
 * ESTADO DEL EDITOR.
 *
 * P1 — El documento canonico es la verdad; el lienzo es una vista de el. Toda
 * accion del usuario modifica ESTE documento y Fabric se sincroniza despues,
 * nunca al reves. Si fuera al reves, el pixel de pantalla acabaria decidiendo
 * el milimetro de produccion.
 *
 * La validacion corre AQUI con el mismo `rules-core` que el servidor (DEC-10):
 * respuesta instantanea en el movil, y el servidor sigue teniendo la ultima
 * palabra al presupuestar y al confirmar.
 */

export interface EditorContext {
  designId: string;
  /** De qué producto y vista es. Lo pide el ajuste del encaje del mockup. */
  productId: string;
  viewCode: string;
  zone: EffectiveZone;
  technique: TechniqueRuleVersion;
  substrate: string;
  productName: string;
  colorId: string;
  colorName: string;
  darkGarment: boolean;
  /**
   * Todos los colores del producto.
   *
   * Ver el mismo diseno en negro y en blanco es la comprobacion mas util que
   * existe, y hoy obliga a volver a la ficha del producto y empezar de cero.
   */
  colores: Array<{
    id: string;
    name: string;
    /** Color aproximado, para la muestra del selector. */
    rgb: { r: number; g: number; b: number };
    dark: boolean;
    imageUrl: string | null;
    /** Cara de la foto de este color (ver `mockup.caraMostrada`). */
    caraMostrada: string | null;
  }>;
  sizes: string[];
  size: string;
  /** Encaje de la foto del modelo con la prenda puesta, si pasó la auditoría. */
  puesto: {
    imageUrl: string;
    mmPerPixel: number;
    anchor: { xFrac: number; yFrac: number };
    provenance: 'inherited' | 'adjusted' | 'exception';
    confidence: number;
  } | null;
  /** Foto de la prenda y encaje del estampado, para la vista sobre prenda. */
  mockup: {
    imageUrl: string | null;
    /**
     * La CARA que muestra la foto (código de vista: `front`, `back`…).
     *
     * Normalmente es la de la zona que se trabaja. Pero si de esa cara no hay
     * foto para el color elegido, se cae a la delantera: entonces se está
     * mirando el pecho de la prenda mientras se estampa la espalda, y eso hay
     * que DECIRLO, no enseñar la cara equivocada en silencio.
     */
    caraMostrada: string | null;
    mmPerPixel: number;
    anchor: { xFrac: number; yFrac: number } | null;
    /**
     * Cuánto se aparta ESTA zona del ancla de su vista, en milímetros.
     *
     * Va aquí y no en `zone` porque es un dato de encaje del mockup, como el
     * ancla y la escala; `EffectiveZone` es el contrato de validación, el mismo
     * en el navegador y en el servidor, y no debe cargar con cómo se dibuja una
     * vista previa.
     */
    zoneOffsetMm: { x: number; y: number };
    provenance: 'inherited' | 'adjusted' | 'exception';
    confidence: number;
  };
}

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

interface EditorState {
  document: CanonicalDocument;
  baseVersion: number;
  selectedLayerId: string | null;
  /**
   * TODAS las capas seleccionadas, en el orden en que se fueron sumando.
   *
   * `selectedLayerId` sigue existiendo y es siempre la ULTIMA de esta lista:
   * el panel de propiedades edita una capa, no seis, y media aplicacion lee ese
   * campo. Mantener los dos evita reescribirla entera para ganar el marco de
   * seleccion.
   */
  seleccionadas: string[];
  /** Escalar sin deformar. Un logotipo estirado no se ve hasta que esta impreso. */
  proporcionBloqueada: boolean;
  context: EditorContext | null;
  saveState: SaveState;
  lastError: string | null;
  validation: ValidationResult;
  /** Estados anteriores del documento, para deshacer. */
  historial: Historial;

  init(context: EditorContext, document: CanonicalDocument, version: number): void;
  select(layerId: string | null): void;
  /** Suma o quita una capa de la seleccion (Shift o Ctrl al pulsar). */
  alternarSeleccion(layerId: string): void;
  /** Sustituye la seleccion entera, para el marco de arrastre. */
  seleccionarVarias(layerIds: string[]): void;
  alinearSeleccion(como: Alineacion): void;
  distribuirSeleccion(eje: 'x' | 'y'): void;
  /** Mueve TODAS las seleccionadas el mismo desplazamiento, en mm. */
  desplazarSeleccion(dxMm: number, dyMm: number): void;
  cambiarProporcionBloqueada(valor: boolean): void;
  /** Marca las seleccionadas como un grupo. Hacen falta dos o mas. */
  agruparSeleccion(): void;
  desagruparSeleccion(): void;
  /** Selecciona una capa arrastrando a todas las de su grupo. */
  seleccionarConGrupo(layerId: string): void;
  /** Sustituye el diseno por una plantilla, ya encajada en la zona. */
  insertarPlantilla(plantillaId: string, tinta: Color, valores?: Record<string, string>): void;
  /** Sustituye TODAS las capas. Lo usa «traer el diseño de otra prenda». */
  reemplazarCapas(layers: Layer[], descripcion: string): void;
  /** Cambia una medida en mm respetando el candado de proporcion. */
  redimensionar(layerId: string, anchoMm: number | null, altoMm: number | null): void;
  addLayer(layer: Layer): void;
  updateLayer(layerId: string, patch: Partial<Layer>): void;
  removeLayer(layerId: string): void;
  duplicateLayer(layerId: string): void;
  moveLayer(layerId: string, direction: 'up' | 'down'): void;
  centerLayer(layerId: string): void;
  /** Cambia el color de la prenda. No toca el documento: el color es el soporte. */
  cambiarColor(color: {
    id: string;
    name: string;
    dark: boolean;
    imageUrl: string | null;
    caraMostrada: string | null;
  }): void;
  /** Deshace el ultimo cambio. Devuelve lo que se deshizo, o null. */
  deshacer(): string | null;
  rehacer(): string | null;
  puedeDeshacer(): boolean;
  puedeRehacer(): boolean;
  revalidate(): void;
  save(): Promise<void>;
  reset(): void;
}

const EMPTY_VALIDATION: ValidationResult = {
  diagnostics: [],
  valid: true,
  counts: { error: 0, warning: 0, info: 0 },
};

/** Autoguardado: se espera a que el usuario pare de tocar antes de escribir. */
export const AUTOSAVE_DEBOUNCE_MS = 1200;

let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

function nextZ(document: CanonicalDocument): number {
  return document.layers.reduce((max, l) => Math.max(max, l.z), -1) + 1;
}

/**
 * Guarda el estado ACTUAL antes de cambiarlo.
 *
 * Se llama antes de cada `set` que toque el documento. Va aparte para no
 * repetir el patron en las ocho acciones que mutan, que es justo donde se
 * olvidaria en la novena.
 */
function anotar(
  get: () => EditorState,
  descripcion: string,
  fusion: string | null = null,
): Partial<EditorState> {
  return { historial: registrar(get().historial, get().document, descripcion, { fusion }) };
}


/**
 * Traslada las capas segun la diferencia entre su caja de antes y la de despues.
 *
 * Se comparten entre alinear y distribuir porque el trabajo es el mismo: lo
 * unico que cambia es quien calcula las cajas nuevas.
 */
function aplicarCajas(
  get: () => EditorState,
  set: (parcial: Partial<EditorState>) => void,
  capas: Layer[],
  antes: Caja[],
  despues: Caja[],
  descripcion: string,
): void {
  const desplazamiento = new Map<string, { dx: number; dy: number }>();
  capas.forEach((capa, i) => {
    const a = antes[i];
    const d = despues[i];
    if (a && d) desplazamiento.set(capa.id, { dx: d.x - a.x, dy: d.y - a.y });
  });

  const document = get().document;
  set({
    ...anotar(get, descripcion),
    document: {
      ...document,
      layers: document.layers.map((l) => {
        const mueve = desplazamiento.get(l.id);
        if (!mueve) return l;
        return { ...l, transform: { ...l.transform, e: l.transform.e + mueve.dx, f: l.transform.f + mueve.dy } } as Layer;
      }),
    },
    saveState: 'pending',
  });
  get().revalidate();
  scheduleSave(get);
}

export const useEditor = create<EditorState>((set, get) => ({
  document: emptyDocument('sin-zona', 100, 100),
  baseVersion: 0,
  selectedLayerId: null,
  seleccionadas: [],
  // Por defecto SI: deformar es casi siempre un accidente, no una decision.
  proporcionBloqueada: true,
  context: null,
  saveState: 'idle',
  lastError: null,
  validation: EMPTY_VALIDATION,
  historial: historialVacio(),

  init(context, document, version) {
    set({
      context,
      document,
      baseVersion: version,
      selectedLayerId: null,
      seleccionadas: [],
      saveState: 'idle',
      lastError: null,
      // Historial LIMPIO al abrir: deshacer no puede llevar al diseño anterior.
      historial: historialVacio(),
    });
    get().revalidate();
  },

  select(layerId) {
    set({ selectedLayerId: layerId, seleccionadas: layerId ? [layerId] : [] });
  },

  alternarSeleccion(layerId) {
    const actuales = get().seleccionadas;
    const siguiente = actuales.includes(layerId)
      ? actuales.filter((id) => id !== layerId)
      : [...actuales, layerId];
    // La ultima que se sumo es la que manda en el panel de propiedades: es la
    // que el usuario acaba de tocar.
    set({ seleccionadas: siguiente, selectedLayerId: siguiente[siguiente.length - 1] ?? null });
  },

  seleccionarVarias(layerIds) {
    set({ seleccionadas: layerIds, selectedLayerId: layerIds[layerIds.length - 1] ?? null });
  },

  cambiarProporcionBloqueada(valor) {
    set({ proporcionBloqueada: valor });
  },

  addLayer(layer) {
    const document = get().document;
    const withZ = { ...layer, z: nextZ(document) } as Layer;
    set({
      ...anotar(get, `anadir ${layer.kind === 'text' ? 'texto' : layer.kind === 'image' ? 'imagen' : 'forma'}`),
      document: { ...document, layers: [...document.layers, withZ] },
      selectedLayerId: withZ.id,
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  updateLayer(layerId, patch) {
    const document = get().document;
    // Los arrastres se funden por capa: treinta cuadros son UN paso deshacible.
    set({
      ...anotar(get, 'mover o cambiar capa', `cambiar:${layerId}`),
      document: {
        ...document,
        layers: document.layers.map((l) => (l.id === layerId ? ({ ...l, ...patch } as Layer) : l)),
      },
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  removeLayer(layerId) {
    const document = get().document;
    set({
      ...anotar(get, 'borrar capa'),
      document: { ...document, layers: document.layers.filter((l) => l.id !== layerId) },
      selectedLayerId: null,
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  duplicateLayer(layerId) {
    const document = get().document;
    const original = document.layers.find((l) => l.id === layerId);
    if (!original) return;
    const copy = {
      ...original,
      id: crypto.randomUUID(),
      z: nextZ(document),
      // Se desplaza 5 mm para que la copia se VEA; si quedara justo encima,
      // el usuario creeria que no ha pasado nada.
      transform: { ...original.transform, e: original.transform.e + 5, f: original.transform.f + 5 },
    } as Layer;
    set({
      ...anotar(get, 'duplicar capa'),
      document: { ...document, layers: [...document.layers, copy] },
      selectedLayerId: copy.id,
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  moveLayer(layerId, direction) {
    const document = get().document;
    const ordered = [...document.layers].sort((a, b) => a.z - b.z);
    const index = ordered.findIndex((l) => l.id === layerId);
    const target = direction === 'up' ? index + 1 : index - 1;
    if (index < 0 || target < 0 || target >= ordered.length) return;

    const a = ordered[index]!;
    const b = ordered[target]!;
    set({
      ...anotar(get, 'cambiar el orden de las capas'),
      document: {
        ...document,
        layers: document.layers.map((l) => (l.id === a.id ? { ...l, z: b.z } : l.id === b.id ? { ...l, z: a.z } : l)),
      },
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  centerLayer(layerId) {
    const { document } = get();
    const layer = document.layers.find((l) => l.id === layerId);
    if (!layer) return;
    const bbox = layerBBoxMm(layer);
    const dx = document.canvasMm.width / 2 - (bbox.minX + bbox.maxX) / 2;
    const dy = document.canvasMm.height / 2 - (bbox.minY + bbox.maxY) / 2;
    get().updateLayer(layerId, {
      transform: { ...layer.transform, e: layer.transform.e + dx, f: layer.transform.f + dy },
    } as Partial<Layer>);
  },

  /*
    ALINEAR, DISTRIBUIR Y DESPLAZAR EN BLOQUE.

    La geometria vive en `encaje.ts`, en milimetros y sin saber nada de Fabric.
    Aqui solo se traduce: capa -> caja, se aplica, y la diferencia se suma a la
    matriz. Se toca `e` y `f` y nada mas —escala y giro se quedan como estaban—
    porque alinear es MOVER, y un alineado que ademas redimensiona seria una
    sorpresa desagradable.

    Todo el bloque entra como UN paso del historial: deshacer un alineado de
    cinco capas no puede exigir cinco Ctrl+Z.
  */
  alinearSeleccion(como) {
    const { document, seleccionadas } = get();
    if (seleccionadas.length === 0) return;

    const capas = document.layers.filter((l) => seleccionadas.includes(l.id));
    if (capas.length === 0) return;

    const cajas = capas.map((l) => {
      const b = layerBBoxMm(l);
      return { x: b.minX, y: b.minY, anchoMm: b.maxX - b.minX, altoMm: b.maxY - b.minY };
    });
    const zona = { minX: 0, minY: 0, maxX: document.canvasMm.width, maxY: document.canvasMm.height };
    const colocadas = alinear(cajas, zona, como);

    aplicarCajas(get, set, capas, cajas, colocadas, 'alinear');
  },

  distribuirSeleccion(eje) {
    const { document, seleccionadas } = get();
    const capas = document.layers.filter((l) => seleccionadas.includes(l.id));
    // Con menos de tres no hay nada que repartir: el hueco lo definen los extremos.
    if (capas.length < 3) return;

    const cajas = capas.map((l) => {
      const b = layerBBoxMm(l);
      return { x: b.minX, y: b.minY, anchoMm: b.maxX - b.minX, altoMm: b.maxY - b.minY };
    });
    aplicarCajas(get, set, capas, cajas, distribuir(cajas, eje), 'repartir el espacio');
  },

  desplazarSeleccion(dxMm, dyMm) {
    const { document, seleccionadas } = get();
    if (seleccionadas.length === 0) return;
    set({
      ...anotar(get, 'mover', `desplazar:${seleccionadas.join(',')}`),
      document: {
        ...document,
        layers: document.layers.map((l) =>
          seleccionadas.includes(l.id)
            ? ({ ...l, transform: { ...l.transform, e: l.transform.e + dxMm, f: l.transform.f + dyMm } } as Layer)
            : l,
        ),
      },
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  /*
    MEDIDA EXACTA en milimetros.

    Escribir "quiero que mida 90 mm de ancho" es la unica forma de acertar: a
    ojo, un logotipo de 88 mm y uno de 92 se ven igual en pantalla y distinto en
    la prenda. Con el candado puesto, el otro lado se calcula solo y la imagen
    no se estira.
  */
  redimensionar(layerId, anchoMm, altoMm) {
    const { document, proporcionBloqueada } = get();
    const capa = document.layers.find((l) => l.id === layerId);
    if (!capa) return;

    const b = layerBBoxMm(capa);
    const anchoActual = b.maxX - b.minX;
    const altoActual = b.maxY - b.minY;
    if (anchoActual <= 0 || altoActual <= 0) return;

    let factorX = anchoMm !== null && anchoMm > 0 ? anchoMm / anchoActual : 1;
    let factorY = altoMm !== null && altoMm > 0 ? altoMm / altoActual : 1;
    if (proporcionBloqueada) {
      // Manda el lado que se escribio; el otro lo sigue.
      const factor = anchoMm !== null ? factorX : factorY;
      factorX = factor;
      factorY = factor;
    }

    const t = capa.transform;
    get().updateLayer(layerId, {
      transform: { ...t, a: t.a * factorX, b: t.b * factorX, c: t.c * factorY, d: t.d * factorY },
    } as Partial<Layer>);
  },

  /*
    AGRUPAR.

    Un escudo son cinco piezas. Sin grupo, moverlo son cinco operaciones y cinco
    ocasiones de descuadrarlo. No se crea un tipo de capa nuevo: las capas de un
    grupo comparten la etiqueta `name`, que ya existe en el esquema desde la v2.
    Un grupo de verdad obligaria a subir el esquema y a que el compositor de
    produccion aprendiera a recorrer un arbol; asi, para el taller siguen siendo
    las mismas capas planas de siempre.
  */
  agruparSeleccion() {
    const { document, seleccionadas } = get();
    if (seleccionadas.length < 2) return;
    set({
      ...anotar(get, 'agrupar'),
      document: { ...document, layers: agrupar(document.layers, seleccionadas, crypto.randomUUID()) },
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  desagruparSeleccion() {
    const { document, selectedLayerId } = get();
    const capa = document.layers.find((l) => l.id === selectedLayerId);
    const grupo = capa ? grupoDe(capa) : null;
    if (!grupo) return;
    set({
      ...anotar(get, 'desagrupar'),
      document: { ...document, layers: desagrupar(document.layers, grupo) },
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  /**
   * Selecciona una capa Y todas las de su grupo.
   *
   * Es lo que hace que un grupo se comporte como un grupo al pincharlo: sin
   * esto, la etiqueta compartida no serviria de nada en el lienzo.
   */
  seleccionarConGrupo(layerId) {
    const companeras = companerasDeGrupo(get().document.layers, layerId);
    if (companeras.length > 1) get().seleccionarVarias(companeras);
    else get().select(layerId);
  },

  /**
   * Inserta una plantilla, sustituyendo lo que hubiera.
   *
   * Sustituye y no anade porque una plantilla es una composicion completa:
   * mezclarla con lo que ya habia dejaria las piezas encima unas de otras y el
   * cliente tendria que limpiar a mano. Deshacer lo devuelve todo.
   */
  insertarPlantilla(plantillaId, tinta, valores) {
    const { document, context } = get();
    const plantilla = buscarPlantilla(plantillaId);
    if (!plantilla || !context) return;

    const capas = plantillaEnZona(plantilla, { widthMm: document.canvasMm.width, heightMm: document.canvasMm.height }, tinta, valores).map((capa, i) => ({ ...capa, z: i }) as Layer);
    set({
      ...anotar(get, 'poner una plantilla'),
      document: { ...document, layers: capas },
      selectedLayerId: null,
      seleccionadas: [],
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  /*
    CAMBIAR EL COLOR de la prenda.

    No entra en el historial ni marca el documento como pendiente: el color NO
    es parte del documento canonico, es el soporte sobre el que se imprime. Lo
    que si hace falta es REVALIDAR —una tinta blanca sobre prenda blanca no se
    ve— y por eso se llama a `revalidate` al terminar.
  */
  cambiarColor(color) {
    const { context } = get();
    if (!context) return;
    set({
      context: {
        ...context,
        colorId: color.id,
        colorName: color.name,
        darkGarment: color.dark,
        mockup: {
          ...context.mockup,
          imageUrl: color.imageUrl ?? context.mockup.imageUrl,
          caraMostrada: color.caraMostrada,
        },
      },
    });
    get().revalidate();
  },

  reemplazarCapas(layers, descripcion) {
    const { document } = get();
    set({
      ...anotar(get, descripcion),
      document: { ...document, layers: layers.map((capa, i) => ({ ...capa, z: i }) as Layer) },
      selectedLayerId: null,
      seleccionadas: [],
      saveState: 'pending',
    });
    get().revalidate();
    scheduleSave(get);
  },

  /*
    Deshacer y rehacer.

    Devuelven QUE se deshizo para poder decirlo en pantalla —«se deshizo: borrar
    capa»—, porque un cambio que ocurre sin explicación deja al usuario sin
    saber si pulsó lo que quería.

    Se marca `pending` y se guarda: deshacer es un cambio como cualquier otro y
    el servidor tiene que enterarse. Si no, cerrar la pestaña despues de
    deshacer recuperaria el estado deshecho.
  */
  deshacer() {
    const { historial, document } = get();
    const r = deshacerPaso(historial, document);
    if (!r.documento) return null;
    set({ historial: r.historial, document: r.documento, saveState: 'pending' });
    get().revalidate();
    scheduleSave(get);
    return r.descripcion;
  },

  rehacer() {
    const { historial, document } = get();
    const r = rehacerPaso(historial, document);
    if (!r.documento) return null;
    set({ historial: r.historial, document: r.documento, saveState: 'pending' });
    get().revalidate();
    scheduleSave(get);
    return r.descripcion;
  },

  puedeDeshacer() {
    return puedeDeshacer(get().historial);
  },

  puedeRehacer() {
    return puedeRehacer(get().historial);
  },

  revalidate() {
    const { document, context } = get();
    if (!context) {
      set({ validation: EMPTY_VALIDATION });
      return;
    }
    try {
      const validation = validateDesign(document, {
        zone: context.zone,
        technique: context.technique,
        substrate: context.substrate,
        // El motor es PURO y no lee el reloj: se le pasa el instante.
        now: new Date().toISOString(),
      });
      set({ validation });
    } catch (err) {
      // Una regla fuera de vigencia lanza: se muestra como error de la pantalla,
      // no se finge que el diseno es valido.
      set({ lastError: (err as Error).message, validation: EMPTY_VALIDATION });
    }
  },

  async save() {
    const { context, document, baseVersion } = get();
    if (!context) return;

    set({ saveState: 'saving' });
    try {
      const result = await api.saveDesignVersion(context.designId, {
        baseVersion,
        document,
        // La clave de idempotencia es el HASH DEL DOCUMENTO: si el autoguardado
        // se reintenta con el mismo contenido, no crea una version nueva.
        clientKey: canonicalHash(document).slice(7, 39),
      });
      set({ baseVersion: result.version, saveState: 'saved', lastError: null });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'VERSION_CONFLICT') {
        set({ saveState: 'conflict', lastError: (err as Error).message });
        return;
      }
      set({ saveState: 'error', lastError: (err as Error).message });
    }
  },

  reset() {
    if (autosaveTimer) clearTimeout(autosaveTimer);
    set({
      document: emptyDocument('sin-zona', 100, 100),
      baseVersion: 0,
      selectedLayerId: null,
      context: null,
      saveState: 'idle',
      validation: EMPTY_VALIDATION,
      lastError: null,
    });
  },
}));

function scheduleSave(get: () => EditorState): void {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => void get().save(), AUTOSAVE_DEBOUNCE_MS);
}

// ------------------------------------------------------------ constructores

export function createImageLayer(input: {
  assetChecksum: string;
  widthPx: number;
  heightPx: number;
  dpi?: number;
  zone: { widthMm: number; heightMm: number };
  minDpi: number;
}): Layer {
  // Se coloca ocupando como mucho el 60 % de la zona Y sin bajar del dpi minimo
  // del proceso: asi la imagen entra ya valida, en vez de entrar con un aviso.
  const maxByZone = input.zone.widthMm * 0.6;
  const maxByDpi = (input.widthPx / input.minDpi) * 25.4;
  const widthMm = Math.max(10, Math.min(maxByZone, maxByDpi));
  const heightMm = (widthMm * input.heightPx) / input.widthPx;

  return {
    id: crypto.randomUUID(),
    kind: 'image',
    z: 0,
    transform: {
      ...IDENTITY_MATRIX,
      a: widthMm,
      d: heightMm,
      e: (input.zone.widthMm - widthMm) / 2,
      f: (input.zone.heightMm - heightMm) / 2,
    },
    anchor: 'top-left',
    opacity: 1,
    blend: 'normal',
    locked: false,
    assetChecksum: input.assetChecksum,
    intrinsic: { widthPx: input.widthPx, heightPx: input.heightPx, ...(input.dpi ? { dpi: input.dpi } : {}) },
  };
}

/**
 * Con que se escribe cuando el cliente no ha elegido nada.
 *
 * `version` identifica la TANDA del catalogo, no la version de la familia: los
 * ficheros los descargamos nosotros de una vez (tools/descargar-fuentes.mjs) y
 * lo que hace reproducible el diseno es el `fileHash`, que son los bytes.
 */
const FAMILIA_POR_DEFECTO = 'Inter';
const VERSION_CATALOGO = '2026-08';

export function createTextLayer(input: {
  text: string;
  color: Color;
  zone: { widthMm: number; heightMm: number };
  sizeMm?: number;
}): Layer {
  const sizeMm = input.sizeMm ?? Math.max(8, Math.round(input.zone.widthMm / 12));
  return {
    id: crypto.randomUUID(),
    kind: 'text',
    z: 0,
    transform: { ...IDENTITY_MATRIX, e: input.zone.widthMm * 0.2, f: input.zone.heightMm * 0.4 },
    anchor: 'top-left',
    opacity: 1,
    blend: 'normal',
    locked: false,
    text: input.text,
    font: { family: FAMILIA_POR_DEFECTO, version: VERSION_CATALOGO, fileHash: hashDeFuente(FAMILIA_POR_DEFECTO, 400, false), weight: 400, italic: false },
    sizeMm,
    tracking: 0,
    lineHeightMm: sizeMm * 1.25,
    direction: 'ltr',
    align: 'left',
    textCase: 'none',
    color: input.color,
  };
}

/**
 * Una forma del catalogo, colocada en la zona.
 *
 * El TAMANO sale de la proporcion natural de cada forma: un escudo apaisado no
 * se lee como escudo, y una banderola cuadrada tampoco es una banderola. Se
 * parte de la mitad del ancho de la zona y el alto se deduce.
 */
export function createShapeLayer(input: {
  shape: NombreForma;
  color: Color;
  zone: { widthMm: number; heightMm: number };
  opciones?: OpcionesForma;
}): Layer {
  const ancho = Math.round(input.zone.widthMm * 0.5);
  const alto = Math.round(ancho / proporcionDeForma(input.shape));

  return {
    id: crypto.randomUUID(),
    kind: 'shape',
    z: 0,
    // Centrada en horizontal y algo por encima del medio, que es donde se
    // empieza a componer: dejarla en una esquina obliga a moverla siempre.
    transform: {
      ...IDENTITY_MATRIX,
      e: (input.zone.widthMm - ancho) / 2,
      f: Math.max(0, (input.zone.heightMm - alto) / 2),
    },
    anchor: 'top-left',
    opacity: 1,
    blend: 'normal',
    locked: false,
    path: trazadoDeForma(input.shape, ancho, alto, input.opciones ?? {}),
    ...(esSoloTrazo(input.shape) ? { stroke: input.color, strokeWidthMm: 2 } : { fill: input.color }),
  };
}

/** Diagnosticos de una capa concreta, para pintarlos junto a ella. */
export function diagnosticsForLayer(diagnostics: Diagnostic[], layerId: string): Diagnostic[] {
  return diagnostics.filter((d) => d.layerId === layerId);
}

export function polygonBounds(polygon: PointMm[]): { widthMm: number; heightMm: number } {
  const xs = polygon.map((p) => p.x);
  const ys = polygon.map((p) => p.y);
  return { widthMm: Math.max(...xs) - Math.min(...xs), heightMm: Math.max(...ys) - Math.min(...ys) };
}
